import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import type { VideoFirstHeroCreator } from '#src/app/pages/petitionDetails/pageContext';
import { usePetitionSignatureCount, usePhoto } from '#src/app/pages/petitionDetails/shared/hooks';
import { getTikTokPlayerUrl } from '#src/app/shared/utils/socialMedia';

export type VideoFirstViewport = 'desktop' | 'mobile';

type SignatureTally = Readonly<{ displayed: number; displayedLocalized: string }>;

type Result = ModelHookResult<{
	coverUrl: string | undefined;
	creatorName: string | undefined;
	playerUrl: string | undefined;
	signatureCount: SignatureTally;
	signatureGoal: SignatureTally;
	title: string;
	titleDataQa: string | undefined;
}>;

function getTitleDataQa(hasPhoto: boolean, viewport: VideoFirstViewport): string | undefined {
	if (hasPhoto) return viewport === 'desktop' ? 'petition-title-desktop' : 'petition-title-mobile';

	return viewport === 'mobile' ? 'petition-title' : undefined;
}

/**
 * The badge credits the social creator whose video the petition was built from, so it
 * shows their handle (the design renders it bare, without the leading `@`) and falls
 * back to their display name. Returns undefined when the angle metadata carries
 * neither — the badge is then omitted rather than crediting the wrong person.
 */
function getCreatorName(creator: VideoFirstHeroCreator | undefined): string | undefined {
	const handle = creator?.username?.trim().replace(/^@/, '');

	return handle || creator?.name?.trim() || undefined;
}

export function useVideoFirstHero(sourceUrl: string, viewport: VideoFirstViewport): Result {
	const {
		data: {
			petition: { ask, displayTitle, photo },
			videoFirstHero,
		},
	} = usePetitionDetailsPageData();
	const petitionPhoto = usePhoto();
	const { signatureCount, signatureGoal } = usePetitionSignatureCount();
	const playerUrl = getTikTokPlayerUrl(sourceUrl);
	const hasPhoto = photo !== null;
	const heroData = videoFirstHero.enabled ? videoFirstHero : undefined;

	return {
		data: {
			coverUrl: heroData ? petitionPhoto?.url : undefined,
			creatorName: getCreatorName(heroData?.creator),
			playerUrl,
			signatureCount,
			signatureGoal,
			title: hasPhoto ? displayTitle : ask,
			titleDataQa: getTitleDataQa(hasPhoto, viewport),
		},
	};
}
